import {
  getCurrentPhotoFillCoveredSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

export interface FrameSize {
  width: number
  height: number
}

/**
 * The photo fill's cover-fit frame: the LARGER of `shape.width/height` (the shape's own declared
 * box — what `resolvePhotoFillRenderedSize` in stowed-element-builder.ts saves, and what legacy
 * itself cover-fits against uniformly for every shape kind, confirmed against `odctrl.js`'s
 * drop-photo handlers: `r = max(path.rect.width/imgW, path.rect.height/imgH)` — no narrower
 * reference for regular polygons) and the shell's measured path bbox. These normally match for a
 * rectangle, but diverge in two different directions:
 * - A regular polygon's vertices are inscribed in an ellipse matching its own declared box (see
 *   `regularPolygonCommands`), so its bbox is *permanently* smaller than `shape.width/height` in
 *   one or both axes — using the tighter bbox there under-covers the shape relative to what was
 *   saved/reloaded (a photo that looks correctly framed at drop time but shifts once the bbox
 *   settles back to the full box on reload, or vice versa).
 * - Mid-resize, the bbox can lag *larger* for a render or two while the path's `d` finishes an
 *   async recompute — using `shape.width/height` there under-sizes the photo relative to the
 *   still-larger clip, showing a gap.
 * Taking the max of both satisfies both cases: a polygon's bbox can never exceed its own box, so
 * this always resolves to `shape.width/height` for polygons (matching legacy), while still
 * preferring the momentarily-larger bbox during a resize lag.
 */
export function computePhotoFillFrameSize(shape: FrameSize, shellBounds: FrameSize): FrameSize {
  return {
    width: Math.max(shape.width, shellBounds.width),
    height: Math.max(shape.height, shellBounds.height),
  }
}

/**
 * The size the photo actually renders at (its covered box), not the frame — see
 * `photoFillImageTransform`'s doc comment. `preserveAspectRatio` alone can only crop within
 * whatever box the `<image>` is declared at; a transform on a frame-sized image can never reveal
 * photo content beyond that crop, so a real pan (nonzero offsetX/offsetY) would always uncover a
 * gap otherwise. Null when the shape has no photo fill.
 */
export function computePhotoFillCoveredSize(
  photoFill: ShapeData['photoFill'],
  frame: FrameSize
): ReturnType<typeof getCurrentPhotoFillCoveredSize> {
  if (!photoFill) {
    return null
  }

  const { naturalWidth, naturalHeight } = resolveEffectivePhotoFillNaturalSize(photoFill)

  return getCurrentPhotoFillCoveredSize(
    frame.width,
    frame.height,
    naturalWidth,
    naturalHeight,
    Math.max(photoFill.scale, 1)
  )
}
